import { useEffect, useRef } from 'react';
import { auth } from '../../integrations/firebase';

/**
 * SessionGuard — one browser, one signed-in user.
 *
 * Firebase persists the session per ORIGIN, not per tab, so signing in as a second
 * person replaces the first everywhere. The tabs already open do not re-render their
 * identity, though: they keep showing the previous person's name, role and sidebar
 * while every request they send carries the NEW person's token.
 *
 * That is worse than a cosmetic glitch. An agent's screen can sit in front of someone
 * holding an admin token (or the reverse), and whatever they do is recorded against the
 * wrong person. So: if the signed-in uid stops matching the one this tab rendered, the
 * tab reloads. A reload is heavy-handed, and deliberately so — it is the only way to be
 * certain the permissions, the sidebar and the audit trail all describe the same person.
 */
export default function SessionGuard() {
    const bootUid = useRef(undefined);   // undefined = not yet known

    useEffect(() => {
        const reloadFor = (why) => {
            console.warn(`[session] ${why} — reloading so this tab matches the signed-in user`);
            window.location.reload();
        };

        const check = () => {
            const now = auth.currentUser?.uid || null;
            if (bootUid.current === undefined) { bootUid.current = now; return; }
            if (bootUid.current === now) return;

            // Signed out elsewhere: let the route guard take them to /login.
            if (!now) { bootUid.current = null; reloadFor('signed out in another tab'); return; }
            // A different person signed in on this browser.
            reloadFor('a different user signed in');
        };

        const unsub = auth.onAuthStateChanged(() => check());

        // A backgrounded tab can miss the auth broadcast entirely (browsers freeze
        // inactive tabs), so the identity is re-checked whenever it comes forward.
        const onVisible = () => { if (document.visibilityState === 'visible') check(); };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', onVisible);

        return () => {
            unsub();
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', onVisible);
        };
    }, []);

    return null;
}
